import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import currency from 'currency.js';
import {
  defer,
  EMPTY,
  expand,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  reduce,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  toArray,
} from 'rxjs';

import { retryInvalidatedNavigationReads } from '../../../services/navigation-reads.service';
import { hasSignedDealAgreement } from '../../../tools/deal-agreement';
import {
  AdvanceActivityRow,
  AdvanceTransactionService,
} from '../../advance/services/advance-transaction.service';
import { IDealList } from '../../advance-application/models/deal.interface';
import { DealStatusEnum } from '../../advance-application/models/deal-status.enum';
import { DealService } from '../../advance-application/services/deal.service';
import { ProductEnum } from '../../authentication/models/product.enum';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { AccountService } from '../../shared/services/account.service';
import { TransactionService } from '../../shared/services/transaction.service';

/** Use the existing per-deal read model: it includes legacy allocations and provider outcomes. */
export function hasRecordedAdvanceTransfer(
  rows: AdvanceActivityRow[],
): boolean {
  return rows.some(
    (row) =>
      row.kind === 'transaction' &&
      ['Transfer', 'Instant transfer'].includes(row.title) &&
      ['Processing', 'Paid'].includes(row.status),
  );
}

export interface HomeAdvance {
  deal: IDealList;
  signed: boolean;
  available: currency;
}

/** One session-scoped snapshot shared by Home and its navigation indicator. */
@Injectable({ providedIn: 'root' })
export class HomeAdvanceService implements OnDestroy {
  public userId?: number;
  public items: HomeAdvance[] = [];
  public total = currency(0);
  public loading = false;
  public failed = false;
  private request?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private auth: AuthenticationService,
    private deals: DealService,
    private accounts: AccountService,
    private history: AdvanceTransactionService,
    private transactions: TransactionService,
    private router: Router,
  ) {
    this.auth.profile
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh(true));
    this.transactions.transactionUpdated
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh(true));
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refresh(true));
  }

  public get pending(): boolean {
    return (
      this.userId === this.auth.getProfileState()?.id && this.items.length > 0
    );
  }

  public refresh(force = false): void {
    const user = this.auth.getProfileState();
    if (this.loading && this.userId === user?.id && !force) return;
    this.request?.unsubscribe();
    if (this.userId !== user?.id) {
      this.items = [];
      this.total = currency(0);
    }
    this.userId = user?.id;
    this.failed = false;
    this.loading = false;
    if (
      !user?.id ||
      !user.productIds?.some((product) =>
        [ProductEnum.Advance, ProductEnum.Payment].includes(product),
      )
    ) {
      this.items = [];
      this.total = currency(0);
      return;
    }
    this.loading = true;
    const userId = user.id;
    const page = (offset: number) =>
      this.deals.getList({
        limit: 200,
        offset,
        sortBy: 'firstApprovedDate',
        sortDirection: 'desc',
        status: [DealStatusEnum.Approved, DealStatusEnum.GracePeriod],
      });
    const deals = page(0).pipe(
      expand((result, index) =>
        result.list.length && (index + 1) * 200 < result.total
          ? page((index + 1) * 200)
          : EMPTY,
      ),
      reduce((all, result) => all.concat(result.list), [] as IDealList[]),
    );
    this.request = defer(() =>
      forkJoin({
        deals,
        balance: this.accounts.getBalance(undefined, true),
        ledger: this.accounts.getBalanceByDeals(undefined, true),
      }),
    )
      .pipe(
        switchMap(({ deals, balance, ledger }) =>
          from(
            deals.filter(
              (deal) =>
                currency(deal.approvedLimitAmount).value > 0 ||
                currency(ledger[deal.id]?.limit ?? 0).value > 0,
            ),
          ).pipe(
            mergeMap((deal) => {
              const signed = hasSignedDealAgreement(deal);
              // Assigned terms still need signing even when an older agreement was signed.
              const drawn = signed
                ? this.history
                    .activity(deal.id)
                    .pipe(map(hasRecordedAdvanceTransfer))
                : of(false);
              return drawn.pipe(
                map((advanced) => ({
                  advanced,
                  deal,
                  signed,
                  available: currency(
                    Math.max(
                      0,
                      Math.min(
                        currency(
                          ledger[deal.id]?.limit ?? deal.approvedLimitAmount,
                        ).subtract(ledger[deal.id]?.unpaidPrincipal ?? 0).value,
                        balance.available.value,
                      ),
                    ),
                  ),
                })),
              );
            }, 4),
            toArray(),
            map((rows) => {
              const items = rows
                .filter((row) => !row.advanced)
                .sort(
                  (a, b) =>
                    Number(a.signed) - Number(b.signed) ||
                    new Date(b.deal.firstApprovedDate).getTime() -
                      new Date(a.deal.firstApprovedDate).getTime() ||
                    b.deal.id - a.deal.id,
                );
              const total = currency(
                Math.max(
                  0,
                  Math.min(
                    items.reduce(
                      (sum, item) => sum.add(item.available),
                      currency(0),
                    ).value,
                    balance.available.value,
                  ),
                ),
              );
              return { items, total };
            }),
          ),
        ),
        retryInvalidatedNavigationReads(
          () => this.auth.getProfileState()?.id === userId,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ items, total }) => {
          if (this.auth.getProfileState()?.id !== userId) return;
          this.items = items;
          this.total = total;
          this.loading = false;
        },
        error: () => {
          if (this.auth.getProfileState()?.id !== userId) return;
          this.failed = true;
          this.loading = false;
        },
      });
  }

  public ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
